import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';
import {
	useGetAcceptingOrdersQuery,
	useGetAccountsReceivableOrdersQuery,
	useGetApplyingOrdersQuery,
	useGetApprovingOrdersQuery,
	useGetContractOrdersQuery,
	useGetDeliveringOrdersQuery,
	useGetProcessingPendingOrdersQuery,
	useGetPrePaymentOrdersQuery,
	useGetFinalCostVerificationOrdersQuery,
} from '../slices/dashboardApiSlice';
import { useOrderPerformanceFeatures, useOrderViewerCapabilities } from './OrderPerformanceFeaturesProvider';
import { useGetMyRejectedOrdersQuery, useGetOrderDetailsQuery } from '../slices/ordersApiSlice';
import {
	useGetCustomerApplyingApprovalsQuery,
	useGetFinalCostApplyingApprovalsQuery,
	useGetFinalCostRejectedApprovalsQuery,
	useGetRejectedCustomerApprovalsQuery,
	useGetRejectedQuotaApprovalsQuery,
	useGetCustomerApprovalPendingsQuery,
	useGetQuotaApplyingApprovalsQuery,
	useGetQuotaApprovalPendingsQuery,
	useGetFinalCostApprovalPendingsQuery,
} from '../slices/approvalsApiSlice';
import { mapCustomerApprovalToPendingRow, mapFinalCostApprovalToPendingRow, mapQuotaApprovalToPendingRow, mapRejectedFinalCostApprovalToPendingRow } from '../utils/approval/approvalListHub';
import { setCurrentOrderOId, clearCurrentOrderOId } from '../slices/orderSlice';
import {
	canAccessFinalCostVerificationDashboard,
	canQueryDashboardOrderSlice,
	hasPermission,
	PERM,
} from '../utils/accessPermissions';
import {
	shouldFetchProcessingPendingAggregate,
	shouldShowContractPendingTab,
	shouldShowDeliveringPendingTab,
} from '../utils/dashboard/dashboardOrderPendingTabs';

export const OrderContext = createContext({
	order: [],
	setCurrentOrderOIdState: (_orderOId) => {},
});

/** 待办 Tab eventKey → 数据源门闩 */
export const PENDING_TAB_FETCH_KEY = {
	approvalPending: 'approving',
	prePaymentOrder: 'prepayment',
	ContractPending: 'contract',
	acceptPending: 'accepting',
	accountsReceivablePending: 'ar',
	accountsReceivablePendingFinance: 'ar',
	finalCostVerification: 'finalCost',
	deliveryPending: 'delivering',
	Applying: 'applying',
	Rejected: 'rejected',
	ProcessingOrderBuyer: 'processing',
	ProcessingOrderFinance: 'processing',
};

const buildPrimedPendingFetchKeys = (role) => {
	switch (role) {
		case 'manager':
		case 'admin':
			return new Set(['approving', 'rejected']);
		case 'finance':
			return new Set(['prepayment']);
		case 'buyer':
			return new Set(['contract']);
		case 'sale':
		case 'assistant':
			return new Set(['applying', 'rejected']);
		default:
			return new Set();
	}
};

const OrderProvider = ({ children }) => {
	const { userInfo } = useSelector((state) => state.auth);
	const orderPerformanceFeatures = useOrderPerformanceFeatures();
	const viewerCapabilities = useOrderViewerCapabilities();
	const role = userInfo?.role;
	const isDashboardPage = Boolean(useMatch('/dashboard'));

	const [pendingFetchExpanded, setPendingFetchExpanded] = useState(false);
	const [pendingFetchKeys, setPendingFetchKeys] = useState(() => buildPrimedPendingFetchKeys(role));

	useEffect(() => {
		setPendingFetchExpanded(false);
		setPendingFetchKeys(buildPrimedPendingFetchKeys(role));
	}, [role, isDashboardPage]);

	const allowPendingFetch = useCallback((key) => pendingFetchExpanded || pendingFetchKeys.has(key), [pendingFetchExpanded, pendingFetchKeys]);

	const enablePendingLoad = useCallback((key) => {
		if (!key) return;
		setPendingFetchKeys((prev) => {
			if (prev.has(key)) return prev;
			const next = new Set(prev);
			next.add(key);
			return next;
		});
	}, []);

	const expandAllPendingLoads = useCallback(() => {
		setPendingFetchExpanded(true);
	}, []);

	const shouldFetchApprovals =
		isDashboardPage && canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPROVING) && allowPendingFetch('approving');
	const shouldFetchDelivering = isDashboardPage && shouldShowDeliveringPendingTab(userInfo) && allowPendingFetch('delivering');
	const shouldFetchAccepting =
		isDashboardPage && canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_ACCEPTING) && allowPendingFetch('accepting');
	const shouldFetchAR = isDashboardPage && canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_AR) && allowPendingFetch('ar');
	const shouldFetchApplying =
		isDashboardPage &&
		canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_APPLING) &&
		allowPendingFetch('applying');
	const shouldFetchRejected =
		isDashboardPage && canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_REJECTED) && allowPendingFetch('rejected');
	const shouldFetchPrePayment =
		isDashboardPage && canQueryDashboardOrderSlice(userInfo, PERM.DASHBOARD_ORDER_PREPAYMENT) && allowPendingFetch('prepayment');
	const shouldFetchFinalCostVerification =
		isDashboardPage &&
		orderPerformanceFeatures.module &&
		(viewerCapabilities.canAccessFinalCostVerificationDashboard || canAccessFinalCostVerificationDashboard(userInfo)) &&
		allowPendingFetch('finalCost');
	const shouldFetchContract = isDashboardPage && shouldShowContractPendingTab(userInfo) && allowPendingFetch('contract');
	const shouldFetchProcessingPending =
		isDashboardPage && shouldFetchProcessingPendingAggregate(userInfo) && allowPendingFetch('processing');

	const dispatch = useDispatch();
	const { currentOrderOId } = useSelector((state) => state.order);
	const normalizedOrderOId = currentOrderOId ? String(currentOrderOId) : '';
	const {
		data: order = {},
		refetch,
		isError,
		error: orderDetailsError,
		isLoading,
		isFetching: isOrderFetching,
	} = useGetOrderDetailsQuery(normalizedOrderOId, { skip: normalizedOrderOId === '' || normalizedOrderOId === undefined });

	/**
	 * 订单详情已与当前选中订单对齐即可操作。
	 * 后台 Socket 触发的 refetch 不再整页遮罩（仅首屏 isLoading 阻塞）。
	 */
	const isOrderReady = useMemo(() => {
		if (!normalizedOrderOId || normalizedOrderOId === '') {
			return false;
		}
		const orderId = order?._id;
		if (!orderId || String(orderId) !== normalizedOrderOId) {
			return false;
		}
		if (isLoading) {
			return false;
		}
		return true;
	}, [normalizedOrderOId, isLoading, order?._id]);

	const orderItemsGroupedByBrand = useMemo(() => order?.uiHints?.orderItemsGroupedByBrand ?? [], [order?.uiHints?.orderItemsGroupedByBrand]);

	const setCurrentOrderOIdState = useCallback(
		(orderOId) => {
			dispatch(setCurrentOrderOId(orderOId));
		},
		[dispatch],
	);
	const clearCurrentOrderOIdState = useCallback(
		(orderOId) => {
			dispatch(clearCurrentOrderOId(orderOId));
		},
		[dispatch],
	);

	const {
		data: processingPendingData,
		isLoading: loadingProcessingPending,
		error: loadingProcessingPendingError,
		isUninitialized: uninitializedProcessingPending,
		refetch: refetchProcessingPending,
	} = useGetProcessingPendingOrdersQuery(undefined, {
		skip: !shouldFetchProcessingPending,
	});
	const processingPendingBuyerOrders = useMemo(() => processingPendingData?.buyer ?? [], [processingPendingData?.buyer]);
	const processingPendingFinanceOrders = useMemo(() => processingPendingData?.finance ?? [], [processingPendingData?.finance]);

	const {
		data: approvingOrdersRaw = [],
		isLoading: isLoadingApprovingOrders,
		isError: isLoadingApprovingOrdersError,
		refetch: refetchApprovingOrders,
		isFetching: isApprovingOrdersFetching,
		isUninitialized: isApprovingOrdersUninitialized,
	} = useGetApprovingOrdersQuery(undefined, {
		skip: !shouldFetchApprovals,
	});

	const {
		data: customerApprovalPendingsRaw = [],
		isLoading: isLoadingCustomerApprovalPendings,
		isError: isLoadingCustomerApprovalPendingsError,
		refetch: refetchCustomerApprovalPendings,
		isFetching: isCustomerApprovalPendingsFetching,
		isUninitialized: isCustomerApprovalPendingsUninitialized,
	} = useGetCustomerApprovalPendingsQuery(undefined, {
		skip: !shouldFetchApprovals,
	});

	const {
		data: quotaApprovalPendingsRaw = [],
		isLoading: isLoadingQuotaApprovalPendings,
		isError: isLoadingQuotaApprovalPendingsError,
		refetch: refetchQuotaApprovalPendings,
		isFetching: isQuotaApprovalPendingsFetching,
		isUninitialized: isQuotaApprovalPendingsUninitialized,
	} = useGetQuotaApprovalPendingsQuery(undefined, {
		skip: !shouldFetchApprovals,
	});

	const {
		data: finalCostApprovalPendingsRaw = [],
		isLoading: isLoadingFinalCostApprovalPendings,
		isError: isLoadingFinalCostApprovalPendingsError,
		refetch: refetchFinalCostApprovalPendings,
		isFetching: isFinalCostApprovalPendingsFetching,
		isUninitialized: isFinalCostApprovalPendingsUninitialized,
	} = useGetFinalCostApprovalPendingsQuery(undefined, {
		skip: !shouldFetchApprovals,
	});

	const myApprovals = useMemo(() => {
		const orderRows = (Array.isArray(approvingOrdersRaw) ? approvingOrdersRaw : []).map((order) => ({
			...order,
			entryKind: 'order',
		}));
		const customerRows = (Array.isArray(customerApprovalPendingsRaw) ? customerApprovalPendingsRaw : []).map(mapCustomerApprovalToPendingRow).filter(Boolean);
		const quotaRows = (Array.isArray(quotaApprovalPendingsRaw) ? quotaApprovalPendingsRaw : []).map(mapQuotaApprovalToPendingRow).filter(Boolean);
		const finalCostRows = (Array.isArray(finalCostApprovalPendingsRaw) ? finalCostApprovalPendingsRaw : []).map(mapFinalCostApprovalToPendingRow).filter(Boolean);
		return [...quotaRows, ...customerRows, ...finalCostRows, ...orderRows];
	}, [approvingOrdersRaw, customerApprovalPendingsRaw, quotaApprovalPendingsRaw, finalCostApprovalPendingsRaw]);

	const isLoadingMyApprovals = isLoadingApprovingOrders || isLoadingCustomerApprovalPendings || isLoadingQuotaApprovalPendings || isLoadingFinalCostApprovalPendings;
	const isLoadingMyApprovalsError =
		isLoadingApprovingOrdersError || isLoadingCustomerApprovalPendingsError || isLoadingQuotaApprovalPendingsError || isLoadingFinalCostApprovalPendingsError;
	const isMyApprovalsOrderFetching =
		isApprovingOrdersFetching || isCustomerApprovalPendingsFetching || isQuotaApprovalPendingsFetching || isFinalCostApprovalPendingsFetching;
	const isApprovalsUninitialized =
		isApprovingOrdersUninitialized && isCustomerApprovalPendingsUninitialized && isQuotaApprovalPendingsUninitialized && isFinalCostApprovalPendingsUninitialized;
	const refetchMyApprovals = useCallback(() => {
		refetchApprovingOrders();
		refetchCustomerApprovalPendings();
		refetchQuotaApprovalPendings();
		refetchFinalCostApprovalPendings();
	}, [refetchApprovingOrders, refetchCustomerApprovalPendings, refetchQuotaApprovalPendings, refetchFinalCostApprovalPendings]);

	// get approving orders hook
	const {
		data: deliveringOrders = [],
		isLoading: isLoadingDeliveringOrder,
		isError: isLoadingDeliveringOrderError,
		refetch: refetchDeliveringOrders,
		isFetching: isDeliveringOrderFetching,
		isUninitialized: isDeliveringOrdersUninitialized,
	} = useGetDeliveringOrdersQuery(undefined, {
		skip: !shouldFetchDelivering,
	});

	// get accepting orders hook（接口返回 { orders, acceptAccountsReceivable }，汇总由后端计算以减轻前端加载）
	const {
		data: acceptingData,
		isLoading: isLoadingAcceptingOrder,
		isError: isLoadingAcceptingOrderError,
		refetch: refetchAcceptingOrders,
		isFetching: isAcceptingOrderFetching,
		isUninitialized: isAcceptingOrdersUninitialized,
	} = useGetAcceptingOrdersQuery(undefined, {
		skip: !shouldFetchAccepting,
	});
	const acceptingOrders = useMemo(() => acceptingData?.orders ?? [], [acceptingData?.orders]);
	const acceptAccountsReceivable = acceptingData?.acceptAccountsReceivable ?? 0;
	const acceptTotalQuasiReceivable = acceptingData?.totalQuasiReceivable ?? 0;
	const acceptTotalPriceSum = acceptingData?.totalPriceSum ?? 0;

	// get accounts receivable orders hook（接口返回 { orders, totalAccountsReceivable }）
	const {
		data: arData,
		isLoading: isLoadingAROrders,
		isError: isLoadingAROrdersError,
		refetch: refetchAROrders,
		isFetching: isAROrderFetching,
		isUninitialized: isArOrdersUninitialized,
	} = useGetAccountsReceivableOrdersQuery(undefined, {
		skip: !shouldFetchAR,
	});
	const arOrders = useMemo(() => arData?.orders ?? [], [arData?.orders]);
	const totalAccountsReceivable = arData?.totalAccountsReceivable ?? 0;
	const arTotalQuasiReceivable = arData?.totalQuasiReceivable ?? 0;

	// get applying orders hook
	const {
		data: applyingOrdersRaw = [],
		isLoading: isLoadingApplyingOrdersRaw,
		isError: isLoadingApplyingOrdersRawError,
		refetch: refetchApplyingOrdersRaw,
		isUninitialized: isApplyingOrdersRawUninitialized,
	} = useGetApplyingOrdersQuery(undefined, {
		skip: !shouldFetchApplying,
	});
	const {
		data: customerApplyingRaw = [],
		isLoading: isLoadingCustomerApplying,
		isError: isLoadingCustomerApplyingError,
		refetch: refetchCustomerApplying,
		isUninitialized: isCustomerApplyingUninitialized,
	} = useGetCustomerApplyingApprovalsQuery(undefined, {
		skip: !shouldFetchApplying,
	});
	const {
		data: quotaApplyingRaw = [],
		isLoading: isLoadingQuotaApplying,
		isError: isLoadingQuotaApplyingError,
		refetch: refetchQuotaApplying,
		isUninitialized: isQuotaApplyingUninitialized,
	} = useGetQuotaApplyingApprovalsQuery(undefined, {
		skip: !shouldFetchApplying,
	});
	const {
		data: finalCostApplyingRaw = [],
		isLoading: isLoadingFinalCostApplying,
		isError: isLoadingFinalCostApplyingError,
		refetch: refetchFinalCostApplying,
		isUninitialized: isFinalCostApplyingUninitialized,
	} = useGetFinalCostApplyingApprovalsQuery(undefined, {
		skip: !shouldFetchApplying,
	});
	const applyingOrders = useMemo(() => {
		const orderRows = (Array.isArray(applyingOrdersRaw) ? applyingOrdersRaw : []).map((order) => ({
			...order,
			entryKind: 'order',
		}));
		const customerRows = (Array.isArray(customerApplyingRaw) ? customerApplyingRaw : []).map(mapCustomerApprovalToPendingRow).filter(Boolean);
		const quotaRows = (Array.isArray(quotaApplyingRaw) ? quotaApplyingRaw : []).map(mapQuotaApprovalToPendingRow).filter(Boolean);
		const finalCostRows = (Array.isArray(finalCostApplyingRaw) ? finalCostApplyingRaw : []).map(mapFinalCostApprovalToPendingRow).filter(Boolean);
		return [...quotaRows, ...customerRows, ...finalCostRows, ...orderRows];
	}, [applyingOrdersRaw, customerApplyingRaw, quotaApplyingRaw, finalCostApplyingRaw]);
	const isLoadingApplyingOrders = isLoadingApplyingOrdersRaw || isLoadingCustomerApplying || isLoadingQuotaApplying || isLoadingFinalCostApplying;
	const isLoadingApplyingOrdersError =
		isLoadingApplyingOrdersRawError || isLoadingCustomerApplyingError || isLoadingQuotaApplyingError || isLoadingFinalCostApplyingError;
	const isApplyingOrdersUninitialized =
		isApplyingOrdersRawUninitialized && isCustomerApplyingUninitialized && isQuotaApplyingUninitialized && isFinalCostApplyingUninitialized;
	const refetchApplyingOrders = useCallback(() => {
		refetchApplyingOrdersRaw();
		refetchCustomerApplying();
		refetchQuotaApplying();
		refetchFinalCostApplying();
	}, [refetchApplyingOrdersRaw, refetchCustomerApplying, refetchQuotaApplying, refetchFinalCostApplying]);
	const applicantRejectedOnly =
		hasPermission(userInfo, PERM.DASHBOARD_ORDER_APPLING) &&
		hasPermission(userInfo, PERM.MENU_APPROVAL_RECORDS) &&
		!hasPermission(userInfo, PERM.APPROVAL_QUERY_TENANT_ALL);
	const useHubCustomerRejected = role === 'admin' || hasPermission(userInfo, PERM.APPROVAL_QUERY_TENANT_ALL) || applicantRejectedOnly;
	const {
		data: myRejectedOrdersRaw,
		isLoading: isLoadingMyRejectedOrders,
		isError: isLoadingMyRejectedOrdersError,
		refetch: refetchMyRejectedOrdersRaw,
		isUninitialized: isMyRejectedOrdersUninitialized,
	} = useGetMyRejectedOrdersQuery(undefined, {
		skip: !shouldFetchRejected,
	});
	const rejectedListScope = useHubCustomerRejected ? 'all' : 'mine';
	const {
		data: rejectedCustomerApprovalsRaw = [],
		isLoading: isLoadingRejectedCustomer,
		isError: isLoadingRejectedCustomerError,
		refetch: refetchRejectedCustomer,
		isUninitialized: isRejectedCustomerUninitialized,
	} = useGetRejectedCustomerApprovalsQuery(
		{ scope: rejectedListScope },
		{ skip: !shouldFetchRejected },
	);
	const {
		data: rejectedQuotaApprovalsRaw = [],
		isLoading: isLoadingRejectedQuota,
		isError: isLoadingRejectedQuotaError,
		refetch: refetchRejectedQuota,
		isUninitialized: isRejectedQuotaUninitialized,
	} = useGetRejectedQuotaApprovalsQuery(
		{ scope: rejectedListScope },
		{ skip: !shouldFetchRejected },
	);
	const {
		data: finalCostRejectedRaw = [],
		isLoading: isLoadingFinalCostRejected,
		isError: isLoadingFinalCostRejectedError,
		refetch: refetchFinalCostRejected,
		isUninitialized: isFinalCostRejectedUninitialized,
	} = useGetFinalCostRejectedApprovalsQuery(undefined, {
		skip: !shouldFetchRejected,
	});
	const rejectedPendingOrders = useMemo(() => {
		const orderRows = (Array.isArray(myRejectedOrdersRaw?.orders) ? myRejectedOrdersRaw.orders : []).map((row) => {
			const at = row.createdAt || null;
			return {
				_id: row._id,
				entryKind: 'order',
				orderId: row.sourceOrderId || '',
				customerInfo: {
					customerName: row.customer?.customerName || '',
					staffName: row.customer?.staffName || '',
					staffPhone: row.customer?.staffPhone || '',
				},
				createdUser: row.creator || null,
				assistantUser: row.assistant || null,
				saleUserInfo: { name: row.sale?.name || row.user?.name || '' },
				approvalStatus: 'reject',
				rejectReason: row.rejectReason || '',
				paymentMethod: row.paymentMethod || '',
				createdAt: at,
				updatedAt: at,
				uiHints: {
					queueEnteredAt: at,
					activityAt: at,
				},
			};
		});
		const customerRows = (Array.isArray(rejectedCustomerApprovalsRaw) ? rejectedCustomerApprovalsRaw : [])
			.filter((row) => {
				if (row?.status !== 'reject') return false;
				if (!applicantRejectedOnly) return true;
				const applicantId = row?.applicant?._id || row?.applicant;
				return String(applicantId || '') === String(userInfo?._id || '');
			})
			.map((row) => {
				const mapped = mapCustomerApprovalToPendingRow(row);
				if (!mapped) return null;
				const at = row.updatedAt || row.createdAt || null;
				return {
					...mapped,
					rejectReason: String(row.approvalSuggestions || '').trim(),
					uiHints: {
						queueEnteredAt: at,
						activityAt: at,
					},
				};
			})
			.filter(Boolean);
		const quotaRows = (Array.isArray(rejectedQuotaApprovalsRaw) ? rejectedQuotaApprovalsRaw : [])
			.filter((row) => {
				if (row?.status !== 'reject') return false;
				if (!applicantRejectedOnly) return true;
				const applicantId = row?.applicant?._id || row?.applicant;
				return String(applicantId || '') === String(userInfo?._id || '');
			})
			.map((row) => {
				const mapped = mapQuotaApprovalToPendingRow(row);
				if (!mapped) return null;
				const at = row.updatedAt || row.createdAt || null;
				return {
					...mapped,
					rejectReason: String(row.approvalSuggestions || '').trim(),
					uiHints: {
						queueEnteredAt: at,
						activityAt: at,
					},
				};
			})
			.filter(Boolean);
		const finalCostRows = (Array.isArray(finalCostRejectedRaw) ? finalCostRejectedRaw : [])
			.map((row) => mapRejectedFinalCostApprovalToPendingRow(row))
			.filter(Boolean);
		return [...quotaRows, ...customerRows, ...finalCostRows, ...orderRows];
	}, [
		rejectedCustomerApprovalsRaw,
		rejectedQuotaApprovalsRaw,
		finalCostRejectedRaw,
		applicantRejectedOnly,
		myRejectedOrdersRaw,
		userInfo?._id,
	]);
	const isLoadingRejectedPendingOrders = isLoadingMyRejectedOrders || isLoadingFinalCostRejected || isLoadingRejectedCustomer || isLoadingRejectedQuota;
	const isLoadingRejectedPendingOrdersError =
		isLoadingMyRejectedOrdersError || isLoadingRejectedCustomerError || isLoadingRejectedQuotaError || isLoadingFinalCostRejectedError;
	const isRejectedPendingOrdersUninitialized =
		isMyRejectedOrdersUninitialized || isRejectedCustomerUninitialized || isRejectedQuotaUninitialized || isFinalCostRejectedUninitialized;
	const refetchMyRejectedOrders = useCallback(() => {
		refetchMyRejectedOrdersRaw();
		refetchFinalCostRejected();
		refetchRejectedCustomer();
		refetchRejectedQuota();
	}, [refetchFinalCostRejected, refetchMyRejectedOrdersRaw, refetchRejectedCustomer, refetchRejectedQuota]);

	// get prepayment orders hook（接口返回 { orders, totalPriceSum, totalPrePaymentAmount, totalQuasiReceivable }）
	const {
		data: prePaymentData,
		isLoading: isLoadingPrePaymentOrder,
		isFetching: isFetchingPrePaymentOrder,
		isError: isLoadingPrePaymentOrderError,
		refetch: refetchPrePaymentOrders,
		isUninitialized: isPrePaymentOrdersUninitialized,
	} = useGetPrePaymentOrdersQuery(undefined, {
		skip: !shouldFetchPrePayment,
	});
	const prePaymentOrders = useMemo(() => prePaymentData?.orders ?? [], [prePaymentData?.orders]);
	const prePaymentTotalPriceSum = prePaymentData?.totalPriceSum ?? 0;
	const prePaymentTotalPrePaymentAmount = prePaymentData?.totalPrePaymentAmount ?? 0;
	const prePaymentTotalQuasiReceivable = prePaymentData?.totalQuasiReceivable ?? 0;

	const {
		data: finalCostVerificationData,
		isLoading: isLoadingFinalCostVerificationOrders,
		isError: isLoadingFinalCostVerificationOrdersError,
		refetch: refetchFinalCostVerificationOrders,
		isUninitialized: isFinalCostVerificationOrdersUninitialized,
	} = useGetFinalCostVerificationOrdersQuery(undefined, {
		skip: !shouldFetchFinalCostVerification,
	});
	const finalCostVerificationOrders = useMemo(() => finalCostVerificationData?.orders ?? [], [finalCostVerificationData?.orders]);
	const finalCostVerificationTotalPriceSum = finalCostVerificationData?.totalPriceSum ?? 0;
	const finalCostVerificationTotalSuggestedFinalCost = finalCostVerificationData?.totalSuggestedFinalCost ?? 0;

	// get contract orders hook（接口返回 { orders, contractAccountsReceivable }，汇总由后端计算以减轻前端加载）
	const {
		data: contractData,
		isLoading: isLoadingContractOrders,
		isError: isLoadingContractOrdersError,
		refetch: refetchContractOrders,
		isFetching: isFetchingContractOrders,
		isUninitialized: isContractOrdersUninitialized,
	} = useGetContractOrdersQuery(undefined, {
		skip: !shouldFetchContract,
	});
	const contractOrders = useMemo(() => contractData?.orders ?? [], [contractData?.orders]);
	const contractAccountsReceivable = contractData?.contractAccountsReceivable ?? 0;
	const contractTotalQuasiReceivable = contractData?.totalQuasiReceivable ?? 0;
	const contractTotalPriceSum = contractData?.totalPriceSum ?? 0;

	const refetchProcessingOrderLists = useCallback(() => {
		const safeRefetch = (refetchFn) => {
			if (!refetchFn) return;
			try {
				refetchFn();
			} catch {
				/* 查询尚未订阅时忽略 */
			}
		};

		const refetchConfig = [
			{ condition: shouldFetchPrePayment, refetch: refetchPrePaymentOrders, priority: true },
			{ condition: shouldFetchFinalCostVerification, refetch: refetchFinalCostVerificationOrders, priority: true },
			{ condition: shouldFetchApplying, refetch: refetchApplyingOrders, priority: true },
			{ condition: shouldFetchRejected, refetch: refetchMyRejectedOrders, priority: true },
			{ condition: shouldFetchApprovals, refetch: refetchMyApprovals },
			{ condition: shouldFetchDelivering, refetch: refetchDeliveringOrders },
			{ condition: shouldFetchAccepting, refetch: refetchAcceptingOrders },
			{ condition: shouldFetchAR, refetch: refetchAROrders },
			{ condition: shouldFetchContract, refetch: refetchContractOrders },
			{ condition: shouldFetchProcessingPending, refetch: refetchProcessingPending, priority: true },
		];

		refetchConfig.filter(({ condition, priority }) => priority && condition).forEach(({ refetch: refetchFn }) => safeRefetch(refetchFn));
		refetchConfig.filter(({ condition, priority }) => !priority && condition).forEach(({ refetch: refetchFn }) => safeRefetch(refetchFn));
	}, [
		shouldFetchPrePayment,
		shouldFetchFinalCostVerification,
		shouldFetchApplying,
		shouldFetchRejected,
		shouldFetchApprovals,
		shouldFetchDelivering,
		shouldFetchAccepting,
		shouldFetchAR,
		shouldFetchContract,
		shouldFetchProcessingPending,
		refetchPrePaymentOrders,
		refetchFinalCostVerificationOrders,
		refetchApplyingOrders,
		refetchMyRejectedOrders,
		refetchMyApprovals,
		refetchDeliveringOrders,
		refetchAcceptingOrders,
		refetchAROrders,
		refetchContractOrders,
		refetchProcessingPending,
	]);

	const contextValue = useMemo(
		() => ({
			order: order
				? {
						...order,
						orderItemsGroupedByBrand,
					}
				: undefined,
			refetch,
			refetchProcessingOrderLists,
			isError,
			orderDetailsError,
			currentOrderOId: normalizedOrderOId,
			isLoading,
			isOrderFetching,
			isOrderReady,
			setCurrentOrderOIdState,
			clearCurrentOrderOIdState,
			processingPendingBuyerOrders,
			processingPendingFinanceOrders,
			loadingProcessingPending,
			loadingProcessingPendingError,
			uninitializedProcessingPending,
			refetchProcessingPending,
			myApprovals,
			isLoadingMyApprovals,
			isLoadingMyApprovalsError,
			isMyApprovalsOrderFetching,
			refetchMyApprovals,
			isApprovalsUninitialized,
			deliveringOrders,
			isLoadingDeliveringOrder,
			isLoadingDeliveringOrderError,
			refetchDeliveringOrders,
			isDeliveringOrderFetching,
			isDeliveringOrdersUninitialized,
			acceptingOrders,
			isLoadingAcceptingOrder,
			isLoadingAcceptingOrderError,
			refetchAcceptingOrders,
			isAcceptingOrderFetching,
			isAcceptingOrdersUninitialized,
			arOrders,
			totalAccountsReceivable,
			arTotalQuasiReceivable,
			isLoadingAROrders,
			isLoadingAROrdersError,
			refetchAROrders,
			isAROrderFetching,
			isArOrdersUninitialized,
			applyingOrders,
			isLoadingApplyingOrders,
			isLoadingApplyingOrdersError,
			refetchApplyingOrders,
			rejectedPendingOrders,
			isLoadingRejectedPendingOrders,
			isLoadingRejectedPendingOrdersError,
			refetchMyRejectedOrders,
			isRejectedPendingOrdersUninitialized,
			isApplyingOrdersUninitialized,
			prePaymentOrders,
			prePaymentTotalPriceSum,
			prePaymentTotalPrePaymentAmount,
			prePaymentTotalQuasiReceivable,
			isLoadingPrePaymentOrder,
			isFetchingPrePaymentOrder,
			isLoadingPrePaymentOrderError,
			refetchPrePaymentOrders,
			isPrePaymentOrdersUninitialized,
			contractOrders,
			contractAccountsReceivable,
			contractTotalQuasiReceivable,
			contractTotalPriceSum,
			isLoadingContractOrders,
			isLoadingContractOrdersError,
			refetchContractOrders,
			isFetchingContractOrders,
			isContractOrdersUninitialized,
			shouldFetchApprovals,
			shouldFetchDelivering,
			shouldFetchAccepting,
			shouldFetchAR,
			shouldFetchApplying,
			shouldFetchRejected,
			shouldFetchPrePayment,
			shouldFetchFinalCostVerification,
			finalCostVerificationOrders,
			finalCostVerificationTotalPriceSum,
			finalCostVerificationTotalSuggestedFinalCost,
			isLoadingFinalCostVerificationOrders,
			isLoadingFinalCostVerificationOrdersError,
			refetchFinalCostVerificationOrders,
			isFinalCostVerificationOrdersUninitialized,
			shouldFetchContract,
			shouldFetchProcessingPending,
			acceptAccountsReceivable,
			acceptTotalQuasiReceivable,
			acceptTotalPriceSum,
			enablePendingLoad,
			expandAllPendingLoads,
		}),
		[
			order,
			orderItemsGroupedByBrand,
			refetch,
			refetchProcessingOrderLists,
			isError,
			orderDetailsError,
			normalizedOrderOId,
			isLoading,
			isOrderFetching,
			isOrderReady,
			setCurrentOrderOIdState,
			clearCurrentOrderOIdState,
			processingPendingBuyerOrders,
			processingPendingFinanceOrders,
			loadingProcessingPending,
			loadingProcessingPendingError,
			uninitializedProcessingPending,
			refetchProcessingPending,
			myApprovals,
			isLoadingMyApprovals,
			isLoadingMyApprovalsError,
			isMyApprovalsOrderFetching,
			refetchMyApprovals,
			isApprovalsUninitialized,
			deliveringOrders,
			isLoadingDeliveringOrder,
			isLoadingDeliveringOrderError,
			refetchDeliveringOrders,
			isDeliveringOrderFetching,
			isDeliveringOrdersUninitialized,
			acceptingOrders,
			isLoadingAcceptingOrder,
			isLoadingAcceptingOrderError,
			refetchAcceptingOrders,
			isAcceptingOrderFetching,
			isAcceptingOrdersUninitialized,
			arOrders,
			totalAccountsReceivable,
			arTotalQuasiReceivable,
			isLoadingAROrders,
			isLoadingAROrdersError,
			refetchAROrders,
			isAROrderFetching,
			isArOrdersUninitialized,
			applyingOrders,
			isLoadingApplyingOrders,
			isLoadingApplyingOrdersError,
			refetchApplyingOrders,
			rejectedPendingOrders,
			isLoadingRejectedPendingOrders,
			isLoadingRejectedPendingOrdersError,
			refetchMyRejectedOrders,
			isRejectedPendingOrdersUninitialized,
			isApplyingOrdersUninitialized,
			prePaymentOrders,
			prePaymentTotalPriceSum,
			prePaymentTotalPrePaymentAmount,
			prePaymentTotalQuasiReceivable,
			isLoadingPrePaymentOrder,
			isFetchingPrePaymentOrder,
			isLoadingPrePaymentOrderError,
			refetchPrePaymentOrders,
			isPrePaymentOrdersUninitialized,
			contractOrders,
			contractAccountsReceivable,
			contractTotalQuasiReceivable,
			contractTotalPriceSum,
			isLoadingContractOrders,
			isLoadingContractOrdersError,
			refetchContractOrders,
			isFetchingContractOrders,
			isContractOrdersUninitialized,
			shouldFetchApprovals,
			shouldFetchDelivering,
			shouldFetchAccepting,
			shouldFetchAR,
			shouldFetchApplying,
			shouldFetchRejected,
			shouldFetchPrePayment,
			shouldFetchFinalCostVerification,
			finalCostVerificationOrders,
			finalCostVerificationTotalPriceSum,
			finalCostVerificationTotalSuggestedFinalCost,
			isLoadingFinalCostVerificationOrders,
			isLoadingFinalCostVerificationOrdersError,
			refetchFinalCostVerificationOrders,
			isFinalCostVerificationOrdersUninitialized,
			shouldFetchContract,
			shouldFetchProcessingPending,
			acceptAccountsReceivable,
			acceptTotalQuasiReceivable,
			acceptTotalPriceSum,
			enablePendingLoad,
			expandAllPendingLoads,
		],
	);

	return <OrderContext.Provider value={contextValue}>{children}</OrderContext.Provider>;
};

OrderProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useOrderContext = () => useContext(OrderContext);

export default OrderProvider;
